import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import './FilterFooter.scss';
import { BdsButton, BdsInput, BdsSwitch } from '@/core/constants/webComponents';

export type SaveResult = 'success' | 'error' | 'duplicate' | 'contentDuplicate' | null;

export interface ContentDuplicateData {
  existingFilterName: string;
}

interface FilterFooterProps {
  isExportMode?: boolean;
  isApplyDisabled?: boolean;
  isSaveChecked: boolean;
  onToggleSave: (checked: boolean) => void;
  onClear: () => void;
  onZoom: () => void;
  onApply: () => void;
  onSave: (name: string) => void;
  activeTab?: 'newSearch' | 'savedFilters';
  onEdit?: () => void;
  selectedFilter?: string;
  filterName: string;
  onFilterNameChange: (value: string) => void;
  saveResult?: SaveResult;
  onSaveResultShown?: () => void;
  contentDuplicateData?: ContentDuplicateData | null;
  // Edit mode
  editMode?: boolean;
  isSaveChangesDisabled?: boolean;
  onSaveChanges?: () => void;
  onDeleteFilter?: () => void;
  hideZoom?: boolean;
  hideSavedFilters?: boolean;
  areActionsDisabled?: boolean;
}

export const FilterFooter: React.FC<FilterFooterProps> = ({
  isExportMode = false,
  isApplyDisabled = false,
  isSaveChecked,
  onToggleSave,
  onClear,
  onZoom,
  onApply,
  onSave,
  activeTab = 'newSearch',
  onEdit,
  selectedFilter = '',
  filterName,
  onFilterNameChange,
  saveResult = null,
  onSaveResultShown,
  contentDuplicateData = null,
  editMode = false,
  isSaveChangesDisabled = true,
  onSaveChanges,
  onDeleteFilter,
  hideZoom = false,
  hideSavedFilters = false,
  areActionsDisabled = false,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const switchRefBase = useRef<any>(null);
  const switchRefOverlay = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const hasFilter = selectedFilter.trim().length > 0;

  const extractInputValue = (event: any): string | undefined => {
    const detailValue = event?.detail?.value;
    if (typeof detailValue === 'string') return detailValue;
    const target = event?.target as HTMLInputElement | undefined;
    if (target && typeof target.value === 'string') return target.value;
    const currentTarget = event?.currentTarget as HTMLInputElement | undefined;
    if (currentTarget && typeof currentTarget.value === 'string') return currentTarget.value;
    return undefined;
  };

  useEffect(() => {
    const nodes = [switchRefBase.current, switchRefOverlay.current].filter(Boolean);
    if (nodes.length === 0) return;
    const handleSwitchChange = (event: any) => {
      const checked = event.detail?.checked;
      if (checked === undefined) return;
      onToggleSave(checked);
    };
    nodes.forEach(node => node.addEventListener('bdsChange', handleSwitchChange));
    return () => { nodes.forEach(node => node.removeEventListener('bdsChange', handleSwitchChange)); };
  }, [onToggleSave, activeTab]);

  useEffect(() => {
    const inputNode = inputRef.current;
    if (!inputNode) return;
    const handleInputChange = (event: any) => {
      const value = extractInputValue(event);
      if (value === undefined) return;
      onFilterNameChange(value);
    };
    inputNode.addEventListener('bdsInput', handleInputChange);
    inputNode.addEventListener('bdsChange', handleInputChange);
    return () => {
      inputNode.removeEventListener('bdsInput', handleInputChange);
      inputNode.removeEventListener('bdsChange', handleInputChange);
    };
  }, [onFilterNameChange, activeTab]);

  useEffect(() => {
    if (!saveResult) return;
    const displayToast = async () => {
      try {
        switch (saveResult) {
          case 'success':
            await showToast({ variant: 'success', title: t('globalFilter.saveFilterSuccessTitle'), text: t('globalFilter.saveFilterSuccessText'), icon: 'check', duration: 8 });
            break;
          case 'error':
            await showToast({ variant: 'error', title: t('globalFilter.saveFilterErrorTitle'), text: t('globalFilter.saveFilterErrorText'), icon: 'error', duration: 8 });
            break;
          case 'duplicate':
            await showToast({ variant: 'warning', title: t('globalFilter.saveFilterDuplicateTitle'), text: t('globalFilter.saveFilterDuplicateText'), icon: 'warning', duration: 8 });
            break;
          case 'contentDuplicate':
            await showToast({ 
              variant: 'warning', 
              title: t('globalFilter.saveFilterContentDuplicateTitle'), 
              text: t('globalFilter.saveFilterContentDuplicateText', { filterName: contentDuplicateData?.existingFilterName ?? '' }), 
              icon: 'warning', 
              duration: 10 
            });
            break;
        }
      } catch (error) {
        console.error('Failed to show save result toast:', error);
      } finally {
        onSaveResultShown?.();
      }
    };
    displayToast();
  }, [saveResult, showToast, t, onSaveResultShown, contentDuplicateData]);

  const handleSaveClick = () => {
    const fallbackValue = inputRef.current?.value || '';
    const trimmedName = (filterName || fallbackValue).trim();

    if (!trimmedName) {
      return;
    }

    onSave(trimmedName);
    onToggleSave(false);
    onFilterNameChange('');
  };

  // ── Edit mode footer ──────────────────────────────────────────────────────
  if (editMode) {
    return (
      <div className="filter-footer filter-footer--edit-mode">
        <div className="filter-footer__actions">
          <BdsButton color="negative" onClick={onDeleteFilter}>
            {t('globalFilter.deleteFilter', 'Excluir')}
          </BdsButton>
          <BdsButton
            color="primary"
            onClick={onSaveChanges}
            disabled={isSaveChangesDisabled}
          >
            {t('globalFilter.saveChanges', 'Salvar alterações')}
          </BdsButton>
        </div>
      </div>
    );
  }

  // ── Normal footer ─────────────────────────────────────────────────────────
  return (
    <div className="filter-footer">
      <div className={`filter-footer__base ${isSaveChecked && activeTab === 'newSearch' ? 'filter-footer__base--disabled' : ''}`}>
        {activeTab === 'newSearch' && !hideSavedFilters && (
          <div className="filter-footer__toggle-row">
            <BdsSwitch ref={switchRefBase} name="save-filter-base" refer="save-filter-base" checked={isSaveChecked} size="short" />
            <span className="toggle-label">{t('globalFilter.addToSaved')}<span className="toggle-label--bolded">{t('globalFilter.addToSavedFilters')}</span></span>
          </div>
        )}

        <div className="filter-footer__actions">
          {activeTab === 'savedFilters' ? (
            <button 
              type="button"
              className="filter-footer__btn-secondary" 
              onClick={onEdit}
              disabled={areActionsDisabled}
            >
              {t('globalFilter.editFilter')}
            </button>
          ) : (
            <button 
              type="button"
              className="filter-footer__btn-secondary" 
              onClick={onClear}
              disabled={areActionsDisabled}
            >
              {t('globalFilter.clear')}
            </button>
          )}
          {!hideZoom && (
            <button 
              type="button"
              className="filter-footer__btn-secondary" 
              onClick={onZoom}
              disabled={areActionsDisabled}
            >
              {t('globalFilter.zoom')}
            </button>
          )}
          <button
            type="button"
            className="filter-footer__btn-primary"
            onClick={onApply}
            disabled={activeTab === 'savedFilters' ? !hasFilter || isApplyDisabled || areActionsDisabled : isApplyDisabled || areActionsDisabled}
          >
            {isExportMode ? t('globalFilter.create') : t('globalFilter.applyFilter')}
          </button>
        </div>
      </div>

      {activeTab === 'newSearch' && (
        <div className={`filter-footer__overlay ${isSaveChecked ? 'filter-footer__overlay--open' : ''}`}>
          <div className="filter-footer__toggle-row">
            <BdsSwitch ref={switchRefOverlay} name="save-filter-overlay" refer="save-filter-overlay" checked={isSaveChecked} size="short" />
            <span className="toggle-label">{t('globalFilter.addToSaved')}<span className="toggle-label--bolded">{t('globalFilter.addToSavedFilters')}</span></span>
          </div>

          <div className="filter-footer__save-form">
            <span className="filter-footer__save-label">{t('globalFilter.filterName')}</span>
            <BdsInput ref={inputRef} placeholder={t('globalFilter.filterNamePlaceholder')} value={filterName} />
            <div className="filter-footer__save-action">
              <button 
                type="button" 
                className="btn-save-text" 
                onClick={handleSaveClick}
                disabled={!filterName.trim()}
              >
                {t('globalFilter.save')}
              </button>
            </div>
          </div>

          <div className="filter-footer__actions">
            <button 
              type="button"
              className="filter-footer__btn-secondary" 
              onClick={onClear}
              disabled={areActionsDisabled}
            >
              {t('globalFilter.clear')}
            </button>
            {!hideZoom && (
              <button 
                type="button"
                className="filter-footer__btn-secondary" 
                onClick={onZoom}
                disabled={areActionsDisabled}
              >
                {t('globalFilter.zoom')}
              </button>
            )}
            <button 
              type="button"
              className="filter-footer__btn-primary" 
              onClick={onApply} 
              disabled={isApplyDisabled || areActionsDisabled}
            >
              {isExportMode ? t('globalFilter.create') : t('globalFilter.applyFilter')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
